import { FC, useCallback, useState } from "react";
import ScreenCameraApp from "./camera-app";
import {
  useGetCameraDetailsQuery,
  useGetDeviceHealthQuery,
  useGetDeviceHealthSimpleQuery,
  useGetLastParkingPhotoQuery,
} from "../../../api/network/vmApiService";
import { useNavigate, useParams } from "react-router-dom";
import ScreenCameraOverview from "./tabs/camera-overview";
import HeaderView from "../../map-asset/headerView";
import { routeUrls } from "../../../navigation/routeUrls";
import { APP_CONFIG } from "../../../constants/constants";
import ScreenCameraHealth from "./camera-health";
import ScreenCameraSoftware from "./camera-software";
import ScreenCameraStorage from "./camera-storage";
import ParkingPhotos from "./modalParkingPhotos";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { toast } from "react-toastify";
import CameraSim from "./tabs/camera-sim";

enum CAMERA_MENU {
  Overview = 1,
  SIM = 2,
  App = 3,
  Health = 4,
  Software = 5,
  Storage = 6,
}

const ScreenCameraManagementDetails: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cameraIdAssetId } = useParams<{ cameraIdAssetId: string }>();
  const [cameraId, assetId] = (cameraIdAssetId || "")?.split("__");

  const [selectedMenuItem, setSelectedMenuItem] = useState<CAMERA_MENU>(
    CAMERA_MENU.Overview
  );

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  const { data: deviceDetailRespsonse, isLoading: isLoadingDeviceData } =
    useGetCameraDetailsQuery(cameraId ?? "", {
      skip: !cameraId,
      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    });

  const dataDeviceDetails =
    deviceDetailRespsonse?.data && deviceDetailRespsonse?.data?.length > 0
      ? deviceDetailRespsonse?.data[0]
      : null;

  const {
    data: dataReportSummaryResponse,
    isLoading: isFetchingReportSummary,
  } = useGetDeviceHealthQuery(cameraId ?? "", {
    skip: !cameraId,
    refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
  });

  const { data: dataHealthSimpleResponse } = useGetDeviceHealthSimpleQuery(
    cameraId ?? "",
    {
      skip: !cameraId,
      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    }
  );

  const { data: parkingPhotoResponse } = useGetLastParkingPhotoQuery(
    cameraId ?? "",
    {
      skip: !cameraId,
      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    }
  );

  const onLinkCallback = useCallback(
    (menuItem: CAMERA_MENU) => {
      const cameraId = dataDeviceDetails?.cid;
      setSelectedMenuItem(menuItem);
    },
    [dataDeviceDetails]
  );

  const onBackButtonClick = () =>
    navigate(routeUrls.dashboardChildren.cameraChildren.camera_management);

  const MANAGEMENT_MENU = [
    {
      title: "Overview",
      callback: () => onLinkCallback(CAMERA_MENU.Overview),
    },
    {
      title: "SIM",
      callback: () => onLinkCallback(CAMERA_MENU.SIM),
    },
    {
      title: "App",
      callback: () => onLinkCallback(CAMERA_MENU.App),
    },
    {
      title: "Health",
      callback: () => onLinkCallback(CAMERA_MENU.Health),
    },
    {
      title: "Software",
      callback: () => onLinkCallback(CAMERA_MENU.Software),
    },
    {
      title: "Storage",
      callback: () => onLinkCallback(CAMERA_MENU.Storage),
    },
  ];

  const onParkingPhotoClick = () => {
    dispatch(setModalsData({ ...modalsState, showParkingPhotoView: true }));
  };

  return (
    <div
      className={`grid grid-cols-12 rounded-lg bg-gray-100 ${APP_CONFIG.DES.DASH.P_HORIZ} py-4`}
    >
      <HeaderView
        title={dataDeviceDetails?.device_name ?? ""}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
        wrapperClassName="col-span-10 mb-4"
      />
      <AdminFormFieldSubmit
        buttonDimensionClass={"h-10 w-32 px-6"}
        customWrapperClass="col-span-2 mb-2 flex justify-end"
        type="button"
        variant="primary"
        disabled={dataDeviceDetails?.snapshot?.length === 0}
        label={
          <span className="flex justify-center whitespace-nowrap">
            Parking Photos
          </span>
        }
        onClick={onParkingPhotoClick}
      />
      <div className="col-span-12 bg-white rounded-lg grid grid-cols-12 min-h-screen">
        <div className="col-span-3 flex flex-col border-r-2 border-gray-100 rounded-tl-md overflow-hidden">
          {MANAGEMENT_MENU.map((menuItem, index) => {
            return (
              <span
                onClick={menuItem.callback}
                className={`ps-4 font-medium content-center text-sidemenu-text min-h-12 ${selectedMenuItem === index + 1 ? "bg-accent-blue-pale text-sidemenu-submenu-text-active" : "hover:bg-accent-blue-paleO66 cursor-pointer"}`}
                key={index}
              >
                {menuItem.title}
              </span>
            );
          })}
        </div>
        <div className="col-span-9 p-4">
          {selectedMenuItem === CAMERA_MENU.App && (
            <ScreenCameraApp
              dataDeviceDetails={dataDeviceDetails}
              healthTimesStamp={
                dataReportSummaryResponse?.data?.report?.health_times ?? null
              }
            />
          )}
          {selectedMenuItem === CAMERA_MENU.Overview && (
            <ScreenCameraOverview
              cameraData={dataDeviceDetails}
              reportData={dataReportSummaryResponse?.data ?? null}
              simpleHealthData={dataHealthSimpleResponse?.data ?? null}
            />
          )}
          {selectedMenuItem === CAMERA_MENU.Health && (
            <ScreenCameraHealth
              dataDeviceDetails={dataDeviceDetails}
              healthCams={
                dataReportSummaryResponse?.data?.report?.health_cams ?? null
              }
              simpleHealthData={dataHealthSimpleResponse?.data ?? null}
            />
          )}
          {selectedMenuItem === CAMERA_MENU.Software && (
            <ScreenCameraSoftware dataDeviceDetails={dataDeviceDetails} />
          )}
          {selectedMenuItem === CAMERA_MENU.Storage && (
            <ScreenCameraStorage
              cameraId={cameraId ?? ""}
              dataStorageSummary={
                dataReportSummaryResponse?.data?.report?.health_storages ?? null
              }
            />
          )}
          {selectedMenuItem === CAMERA_MENU.SIM && (
            <CameraSim
              cameraData={dataDeviceDetails}
              reportData={dataReportSummaryResponse?.data ?? null}
              simpleHealthData={dataHealthSimpleResponse?.data ?? null}
            />
          )}
        </div>
      </div>
      <ParkingPhotos photos={parkingPhotoResponse?.data?.snapshot ?? []} />
    </div>
  );
};

export default ScreenCameraManagementDetails;
